import { IProductCard } from '@src/components/product-card/product-card.types'
import collectionService from '@src/pages/[...slug]/components/collection/services/collectionService/CollectionService'
import {
  formatSandRecommendationsResponse,
  getProductCardsFromCollection,
} from '@src/pages/[...slug]/components/collection/services/collectionService/utils'
import { getPFYDynamicCollectionId } from '@src/pages/[...slug]/components/collection/utils'
import {
  getNonProductBasedRecommendationId,
  RECOMMENDED_PRODUCTS_TYPE,
} from '@src/quince-site-pdp/src/organisms/productPageSections/utils'

import { ERecommendationToCollectionMapper } from '../../domains/Product/ProductRecommendation'
import ProductCard from '../../domains/ProductCard'
import QuinceApiError from '../../domains/quince-api-error'

import { PRODUCT_FEED_TYPE } from './productFeedService.types'
import {
  getCollectionIdBasedOnFirstProductCard,
  getLbUserIDForCustomerTopProducts,
  getProductRecommendationOptionsForMTEandQSAL,
} from './utils'

export const FEED_TYPE_TO_RECOMMENDATION_TYPE_MAPPER: Record<PRODUCT_FEED_TYPE, RECOMMENDED_PRODUCTS_TYPE> = {
  [PRODUCT_FEED_TYPE.CUSTOMER_TOP_PRODUCTS]: RECOMMENDED_PRODUCTS_TYPE.CUSTOMER_TOP_PRODUCTS,
  [PRODUCT_FEED_TYPE.CUSTOMER_TOP_SIMILAR_PRODUCTS]: RECOMMENDED_PRODUCTS_TYPE.MTE,
  [PRODUCT_FEED_TYPE.FBT]: RECOMMENDED_PRODUCTS_TYPE.QSAL,
  [PRODUCT_FEED_TYPE.CUSTOMER_PERSONALIZED_PRODUCTS]: RECOMMENDED_PRODUCTS_TYPE.BEST_SELLER,
  [PRODUCT_FEED_TYPE.CART_RECOMMENDED]: RECOMMENDED_PRODUCTS_TYPE.CART_RECOMMENDED,
  [PRODUCT_FEED_TYPE.CART_RECOMMENDED_OLD]: RECOMMENDED_PRODUCTS_TYPE.CART_RECOMMENDED_OLD,
  [PRODUCT_FEED_TYPE.UNDER_30]: RECOMMENDED_PRODUCTS_TYPE.UNDER_30,
}

// This can be used to pass some specific data to custom section for Product Feed systematically.
// Note: This will never come from contentful, and should be used only systematically
interface IProductFeedMetaData {
  productCards?: ProductCard[] | undefined
}

export class ProductFeedMetaData implements IProductFeedMetaData {
  productCards?: ProductCard[]

  constructor({ productCards }: IProductFeedMetaData) {
    this.productCards = productCards
    return {
      ...this,
    }
  }
}

class ProductFeedService {
  async getProductFeed(
    isI18nPreviewEnabled: boolean,
    feedType: PRODUCT_FEED_TYPE,
    limit: number,
    recommendationCollectionIdMapper?: ERecommendationToCollectionMapper,
    productCards?: IProductCard[],
    abortSignal?: AbortSignal,
    apiVersion?: string,
    isUltraLiteEnabled?: boolean,
    subscriptionExpEnabled?: boolean,
    isSvdEnabled?: boolean,
  ): Promise<IProductCard[]> {
    // Collection-based path is always used; recommendationCollectionIdMapper must be non-null
    // for any feed to return results.
    if (recommendationCollectionIdMapper) {
      const firstProductCard = productCards?.[0]
      const recommendedProductsType = FEED_TYPE_TO_RECOMMENDATION_TYPE_MAPPER[feedType] ?? ''
      const initialCollectionId = getNonProductBasedRecommendationId(
        recommendationCollectionIdMapper,
        recommendedProductsType,
      )
      const isfirstProductCardReq =
        recommendedProductsType === RECOMMENDED_PRODUCTS_TYPE.MTE ||
        recommendedProductsType === RECOMMENDED_PRODUCTS_TYPE.QSAL

      // `displayConfig` is nullable on an SVD-shaped seed card (no-VDA product) — deref safely.
      const colorOfFirstProductCard = firstProductCard?.product.cardVariants[0]?.displayConfig?.value
      const lbUserId = getLbUserIDForCustomerTopProducts(recommendedProductsType)
      const request = {
        collectionId: getCollectionIdBasedOnFirstProductCard(
          firstProductCard,
          isfirstProductCardReq,
          recommendationCollectionIdMapper,
          initialCollectionId,
        ),
        limit,
        abortSignal,
        // fallback when country is na
        ...(lbUserId && { lbUserId }),
        // For QSAL, send specific parameters, otherwise use default
        ...(recommendedProductsType === RECOMMENDED_PRODUCTS_TYPE.QSAL
          ? {
              productIdForRecommendations: firstProductCard?.product?.productId,
              colorForProductRecommendations: colorOfFirstProductCard,
              isOptimisedRecommendations: true,
              disableFilters: true,
            }
          : getProductRecommendationOptionsForMTEandQSAL(
              isfirstProductCardReq,
              productCards ?? [],
              colorOfFirstProductCard,
            )),
        apiVersion,
      }

      // SVD takes precedence over the ultra-lite bucket: an SVD-bucketed user cannot render legacy
      // cards at all (the shared `ProductCard` wrapper returns null for them), so every SVD session
      // goes to the card-native `/collection/v5` regardless of the `USE_ULTRA_LITE` experiment.
      // `/collection/v5` has no ultra-lite mode, which is why `isUltraLiteEnabled` is not part of
      // the shared request and is applied as a literal on the v3/BFF branch below (the
      // `getCollectionById` overloads discriminate on `true` vs `false | undefined`).
      if (isSvdEnabled) {
        return collectionService.getSvdCollectionCards(request, false, subscriptionExpEnabled)
      }

      // The non-ultra-lite branch passes `request` as-is rather than an explicit
      // `isUltraLiteEnabled: false`: the overload's property is optional, and omitting it keeps the
      // outgoing request byte-identical to before this change.
      const collection = isUltraLiteEnabled
        ? await collectionService.getCollectionById(
            { ...request, isUltraLiteEnabled: true },
            isI18nPreviewEnabled,
            false,
            subscriptionExpEnabled,
          )
        : await collectionService.getCollectionById(request, isI18nPreviewEnabled, false, subscriptionExpEnabled)
      return getProductCardsFromCollection(collection, isUltraLiteEnabled)
    }

    return Promise.resolve([])
  }

  private async fetchPickedForYouFeed(
    limit: number,
    isI18nPreviewEnabled: boolean,
    apiVersion?: string,
    isUltraLiteEnabled?: boolean,
    subscriptionExpEnabled?: boolean,
    isSvdEnabled?: boolean,
  ) {
    try {
      const collectionId = getPFYDynamicCollectionId()
      const request = { collectionId, page: 0, limit, apiVersion }

      if (isSvdEnabled) {
        return await collectionService.getSvdCollectionCards(request, false, subscriptionExpEnabled)
      }

      // Branches kept whole rather than sharing one `data`: the `getCollectionById` overloads return
      // different shapes per `isUltraLiteEnabled`, and each response reader below only accepts its own.
      if (isUltraLiteEnabled) {
        const data = await collectionService.getCollectionById(
          { ...request, isUltraLiteEnabled: true },
          isI18nPreviewEnabled,
          false,
          subscriptionExpEnabled,
        )
        return formatSandRecommendationsResponse(data)
      }

      const data = await collectionService.getCollectionById(
        request,
        isI18nPreviewEnabled,
        false,
        subscriptionExpEnabled,
      )
      return data.subCollections[0]?.productCards ?? []
    } catch (err) {
      throw new QuinceApiError({
        error: err as Error,
        // @i18n-ignore
        message: 'Failed while fetching product feed fetchPickedForYouFeed()',
        // @i18n-ignore
        apiName: 'get-product-feed',
      })
    }
  }

  async getCustomerPersonalizedProductsNewHomePage(
    limit: number,
    recommendationCollectionIdMapper: ERecommendationToCollectionMapper,
    isI18nPreviewEnabled: boolean,
    apiVersion?: string,
    isUltraLiteEnabled?: boolean,
    subscriptionExpEnabled?: boolean,
    isSvdEnabled?: boolean,
  ): Promise<IProductCard[]> {
    if (recommendationCollectionIdMapper) {
      const collectionId = getNonProductBasedRecommendationId(
        recommendationCollectionIdMapper,
        RECOMMENDED_PRODUCTS_TYPE.BEST_SELLER,
      )
      const request = { collectionId, limit, apiVersion }

      if (isSvdEnabled) {
        return collectionService.getSvdCollectionCards(request, false, subscriptionExpEnabled)
      }

      const collection = isUltraLiteEnabled
        ? await collectionService.getCollectionById(
            { ...request, isUltraLiteEnabled: true },
            isI18nPreviewEnabled,
            false,
            subscriptionExpEnabled,
          )
        : await collectionService.getCollectionById(request, isI18nPreviewEnabled, false, subscriptionExpEnabled)
      return getProductCardsFromCollection(collection, isUltraLiteEnabled)
    }

    return this.fetchPickedForYouFeed(
      limit,
      isI18nPreviewEnabled,
      apiVersion,
      isUltraLiteEnabled,
      subscriptionExpEnabled,
      isSvdEnabled,
    ) // TODO: Check this localisation useCase with isI18nPreviewEnabled
  }
}

const productFeedService = new ProductFeedService()

export default productFeedService
